import React from 'react';
import { ArrowRight, Mail } from 'lucide-react';
import { LoginInputRow, LoginLabel, LoginSubmit } from './LoginControls';

/**
 * Login v2 — email step (TASK-201): work-email field + Continue, "or" divider,
 * Sign in with Google. Markup/SVG ported from `design/Login v2.dc.html`.
 */
interface EmailStepProps {
  email: string;
  setEmail: (value: string) => void;
  isLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onGoogle: () => void;
}

const GoogleIcon: React.FC = () => (
  <svg className="h-[16px] w-[16px] shrink-0" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const EmailStep: React.FC<EmailStepProps> = ({ email, setEmail, isLoading, onSubmit, onGoogle }) => (
  <>
    <form onSubmit={onSubmit}>
      <LoginLabel htmlFor="login-email">Email address</LoginLabel>
      <LoginInputRow
        id="login-email"
        icon={<Mail />}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="name@company.com"
        autoComplete="email"
        required
        autoFocus
        className="mb-[16px]"
      />
      <LoginSubmit loading={isLoading} icon={<ArrowRight className="h-[15px] w-[15px]" />}>
        Continue
      </LoginSubmit>
    </form>

    <div className="flex items-center py-[18px]">
      <div className="flex-1 border-t border-neutral-border" />
      <span className="mx-[12px] shrink-0 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[#98A2B3]">
        Or
      </span>
      <div className="flex-1 border-t border-neutral-border" />
    </div>

    <button
      type="button"
      onClick={onGoogle}
      disabled={isLoading}
      className="flex w-full items-center justify-center gap-[10px] rounded-[9px] border border-line-strong bg-surface p-[10px] transition-colors hover:bg-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-action disabled:pointer-events-none disabled:opacity-60"
    >
      <GoogleIcon />
      <span className="text-[13px] font-semibold text-[#344054]">Sign in with Google</span>
    </button>
  </>
);

export default EmailStep;
